import { CreditCard, Info } from 'lucide-react'
import { useState } from 'react'
import { BillingContactCard } from '@/features/billing/components/BillingContactCard'
import { PlanChangeDrawer } from '@/features/billing/components/PlanChangeDrawer'
import type { BillingOverview } from '@/features/billing/types/billing.types'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'

interface OverviewTabProps {
  billingData: BillingOverview | null
  onBillingDataChange: (data: BillingOverview | null) => void
}

export function OverviewTab({
  billingData,
  onBillingDataChange,
}: OverviewTabProps) {
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false)

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getCurrentMonthPeriod = () => {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return {
      start: formatDate(firstDay.toISOString()),
      end: formatDate(lastDay.toISOString()),
    }
  }

  if (!billingData) {
    return (
      <div className='space-y-8'>
        {/* Current Bill and Plan Section Skeleton */}
        <div className='grid gap-8 md:grid-cols-2'>
          {/* Current Bill Total Card Skeleton */}
          <Card className='bg-transparent border'>
            <CardContent className='p-6'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <Skeleton className='h-6 w-32' />
                  <Skeleton className='h-4 w-4 rounded' />
                </div>
                <Skeleton className='h-10 w-24' />
                <Skeleton className='h-4 w-48' />
                <div className='flex justify-end mt-10'>
                  <Skeleton className='h-8 w-56' />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Plan Card Skeleton */}
          <Card className='bg-transparent border'>
            <CardContent className='p-6'>
              <div className='space-y-4'>
                <Skeleton className='h-6 w-28' />
                <Skeleton className='h-8 w-32' />
                <div className='flex justify-between gap-2 mt-20'>
                  <Skeleton className='h-8 w-24' />
                  <Skeleton className='h-8 w-24' />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Billing Contact Skeleton - Half Width */}
        <div className='w-1/2'>
          <Card className='bg-transparent border'>
            <CardContent className='p-6'>
              <div className='space-y-4'>
                <Skeleton className='h-6 w-32' />
                <div className='space-y-3'>
                  <Skeleton className='h-4 w-48' />
                  <Skeleton className='h-4 w-40' />
                  <Skeleton className='h-4 w-44' />
                </div>
                <div className='flex justify-end'>
                  <Skeleton className='h-8 w-36' />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const { start: billingPeriodStart, end: billingPeriodEnd } =
    getCurrentMonthPeriod()

  return (
    <div className='space-y-8'>
      {/* Current Bill and Plan Section */}
      <div className='grid gap-8 md:grid-cols-2'>
        {/* Current Bill Total Card */}
        <Card className='bg-transparent border'>
          <CardContent className='p-6'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <h2 className='text-lg font-medium'>Current bill total</h2>
                <Info className='w-4 h-4 text-muted-foreground' />
              </div>
              <div className='text-4xl font-bold'>
                {formatCurrency(billingData.current_balance)}
              </div>
              <div className='text-sm text-muted-foreground'>
                Billing period: {billingPeriodStart} to {billingPeriodEnd}
              </div>
              <div className='flex justify-end mt-10'>
                <Button variant='outline' size='sm'>
                  <CreditCard className='mr-2 h-4 w-4' />
                  Manage card details and invoices
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Plan Card */}
        <Card className='bg-transparent border'>
          <CardContent className='p-6'>
            <div className='space-y-4'>
              <h2 className='text-lg font-medium'>Current plan</h2>
              <div className='text-2xl font-bold'>Free</div>
              <div>
                {billingData.current_plan.status === 'active' && (
                  <div className='flex justify-between gap-2 mt-20'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPlanDrawerOpen(true)}
                    >
                      Upgrade plan
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='text-red-600 hover:text-red-700'
                    >
                      Cancel plan
                    </Button>
                  </div>
                )}
                {billingData.current_plan.status === 'cancelled' && (
                  <div className='flex justify-center'>
                    <Button size='sm' onClick={() => setPlanDrawerOpen(true)}>
                      Reactivate plan
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Billing Contact - Half Width */}
      <div className='w-1/2'>
        <BillingContactCard
          contact={billingData.billing_contact}
          onContactUpdated={updatedContact => {
            onBillingDataChange(
              billingData
                ? { ...billingData, billing_contact: updatedContact }
                : null
            )
          }}
        />
      </div>

      {/* Account Balance Info */}
      {(billingData.current_balance !== 0 ||
        billingData.overdue_amount > 0) && (
        <Card>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='font-medium'>Account Balance</h3>
                <div className='flex items-center gap-4 mt-2 text-sm'>
                  <span>
                    Current Balance:{' '}
                    {formatCurrency(billingData.current_balance)}
                  </span>
                  {billingData.overdue_amount > 0 && (
                    <span className='text-red-600'>
                      Overdue: {formatCurrency(billingData.overdue_amount)}
                    </span>
                  )}
                </div>
              </div>
              {billingData.overdue_amount > 0 && (
                <Button variant='destructive'>Pay Now</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <PlanChangeDrawer
        open={planDrawerOpen}
        onOpenChange={setPlanDrawerOpen}
        currentPlanId='free'
        onPlanSelected={_planId => {
          // TODO: Update billing data with selected plan
        }}
      />
    </div>
  )
}
